import React, { useContext, useState } from 'react';
import {
  StatistikrapporteringQuery,
  StatistikrapporteringQueryVariables,
  StatistikrapporteringQuery_node_TEDBOAktivitet,
} from './Queries/_QueryTypes/StatistikrapporteringQuery';
import query from './Queries/StatistikrapporteringQuery.gql';
import { TEDBOAktivitetTypEnum } from '../../../../../globalQueryTypes';
import { useQuery } from '@apollo/client';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import {
  StatistikrapporteringAldersindelningQuery,
  StatistikrapporteringAldersindelningQueryVariables,
} from './Queries/_QueryTypes/StatistikrapporteringAldersindelningQuery';
import alderQuery from './Queries/StatistikrapporteringAldersindelningQuery.gql';

import { RutinSettingsContext } from '../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { useSelectedDetailQuery } from '../../../../../Components/QueryComponents/SelectedDetailQuery/useSelectedDetailQuery';
import {
  StatistikrapporteringVy,
  validAktTypeForKomm,
  validGtjCategoriForKomm,
  verksamhetstypHintText,
  verksamhetstypLabelText,
} from './StatistikrapporteringVy';
import { initAktGiltigaAldrar } from './Utils/Utils';
import { ObjektVyGruppContext } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKortContext';
import FunctionItem from '../../../_Common/Components/FunctionMenu/FunctionItem/FunctionItem';
import EDMenuButton from '../../../../../BaseComponents/Buttons/MenuButton/EDMenuButton';
import { createPortal } from 'react-dom';
import { BusinessDataFormContext } from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import EDDialog from '../../../../../BaseComponents/Dialog/EDDialog';
import { getCurrentDateString } from '../../../../../Utils/dateUtils';
import { rutinInfos } from '../../../_Common/Const/RutinInfo';
import { StatAttributeValue } from './Components/StatAttributeValue';
import { StatAlderValue } from './Components/StatAlderValue';
import EDColumns, {
  EDColumn,
} from '../../../../../BaseComponents/Columns/EDColumns';
import EDDivider from '../../../../../BaseComponents/Divider/EDDivider';
import EDIcon from '../../../../../BaseComponents/Icon/EDIcon';

interface IProps {
  id: string;
}

export const StatistikrapporteringCard: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { activeDatabase, availableRoutines } = useContext(
    ActiveDatabaseContext
  );
  const routineSettingsContext = useContext(RutinSettingsContext);
  const objektVyGruppContext = useContext(ObjektVyGruppContext);
  const formContext = useContext(BusinessDataFormContext);
  const [redigeraStatistikOpen, setRedigeraStatistikOpen] =
    useState<boolean>(false);

  const { nodeData, loading: queryLoading } = useSelectedDetailQuery<
    StatistikrapporteringQuery,
    StatistikrapporteringQuery_node_TEDBOAktivitet,
    StatistikrapporteringQueryVariables
  >(query, props.id);

  const { data: alderData, loading: queryAlderLoading } = useQuery<
    StatistikrapporteringAldersindelningQuery,
    StatistikrapporteringAldersindelningQueryVariables
  >(alderQuery, {
    variables: {
      dbname: activeDatabase || '',
      routine: routineSettingsContext.activeRoutine?.routine.rutinID || 0,
    },
    skip: queryLoading || !nodeData?.AldersindeladAktivitet,
  });

  const aktType = nodeData?._typ;
  const digital = nodeData?.DigitalAktivitet || false;
  const aldersindelad = nodeData?.AldersindeladAktivitet || false;

  const aktGiltigaAldrar =
    aldersindelad && !queryAlderLoading && alderData?.db?.BOAldersindelning_All
      ? initAktGiltigaAldrar(nodeData, alderData?.db?.BOAldersindelning_All)
      : [];
  const aktGiltigaAldrarSplit = Math.floor(aktGiltigaAldrar.length / 2);

  const verksamhetsKategori: string =
    (!queryLoading && nodeData?._Associations?.Verksamhetstyp?.CATEGORY) || '';
  const gudstjanstKategori: string =
    (!queryLoading && nodeData?._Associations?.GudstjanstTyp?.CATEGORY) || '';

  const updateStatistikDisabledReason =
    availableRoutines &&
    !availableRoutines[rutinInfos.rtnStatistikrapporteringNew.rutinID]
      ? 'Du har inte behörighet att rapportera statistik'
      : formContext?.formState.editMode && formContext?.isNew
      ? 'Du måste spara aktiviteten för att kunna rapportera statistik'
      : queryLoading || queryAlderLoading
      ? 'Kontrollerar aktivitet...'
      : nodeData?.StatRapSenast <= getCurrentDateString()
      ? 'Senaste datum att rapportera statistik har passerat'
      : formContext?.data?.FRANDATUM > getCurrentDateString()
      ? 'Aktiviteten har inte ägt rum ännu'
      : '';

  return (
    <>
      {objektVyGruppContext &&
        objektVyGruppContext.menuRef.current &&
        createPortal(
          <EDMenuButton
            icon={
              <EDIcon color="secondary" iconType="more_vert" size="medium" />
            }
            tooltip="Funktioner"
            noClickPropagation
            testId={'ActivityStatistikFunctionMenu'}
          >
            <FunctionItem
              onClick={() => setRedigeraStatistikOpen(true)}
              disabled={updateStatistikDisabledReason !== ''}
              disabledReason={updateStatistikDisabledReason}
              testId={'ActivityRegisterStatistik'}
            >
              Registrera statistik
            </FunctionItem>
          </EDMenuButton>,
          objektVyGruppContext.menuRef.current
        )}
      <EDDialog
        open={redigeraStatistikOpen}
        onClose={() => setRedigeraStatistikOpen(false)}
        disableContentWrapperMargin
        title="Registrera statistik"
      >
        <BusinessDataFormContext.Provider value={null}>
          <StatistikrapporteringVy
            id={props.id}
            isEditing
            onValuesCommited={() => setRedigeraStatistikOpen(false)}
            onCancel={() => setRedigeraStatistikOpen(false)}
            isInDialog
          />
        </BusinessDataFormContext.Provider>
      </EDDialog>
      {digital ? ( // Digital aktivitet
        <>
          <StatAttributeValue
            statNodeData={nodeData}
            attributeName="DigitalaVisningar.VISNINGAR_FACEBOOK"
          />
          <StatAttributeValue
            statNodeData={nodeData}
            attributeName="DigitalaVisningar.VISNINGAR_YOUTUBE"
          />
          <StatAttributeValue
            statNodeData={nodeData}
            attributeName="DigitalaVisningar.VISNINGAR_VIMEO"
          />
          <StatAttributeValue
            statNodeData={nodeData}
            attributeName="DigitalaVisningar.VISNINGAR_OVRIG"
          />
          <EDDivider />
          <StatAttributeValue
            statNodeData={nodeData}
            attributeName="DigitalaVisningar.VISNINGAR_TOTAL"
            label="Totalt antal visningar"
          />
        </>
      ) : aldersindelad ? ( // Åldersindelad aktivitet
        <>
          <EDColumns spacing={0}>
            <EDColumn>
              {aktGiltigaAldrar
                .slice(0, aktGiltigaAldrarSplit)
                .map((alder, index) => (
                  <StatAlderValue
                    key={alder.id}
                    statNodeData={nodeData}
                    alderId={alder.id}
                    alderBenamning={alder.BENAMNING}
                  />
                ))}
            </EDColumn>
            <EDColumn>
              {aktGiltigaAldrar
                .slice(aktGiltigaAldrarSplit)
                .map((alder, index) => (
                  <StatAlderValue
                    key={alder.id}
                    statNodeData={nodeData}
                    alderId={alder.id}
                    alderBenamning={alder.BENAMNING}
                  />
                ))}
            </EDColumn>
          </EDColumns>
          <EDDivider />
          <StatAttributeValue
            statNodeData={nodeData}
            attributeName="ANTAL_BESOKARE"
            label="Totalt antal besökare"
          />
        </>
      ) : (
        <>
          <StatAttributeValue
            statNodeData={nodeData}
            attributeName="ANTAL_BESOKARE"
            label={verksamhetstypLabelText(verksamhetsKategori)}
            tooltip={verksamhetstypHintText(verksamhetsKategori)}
          />
          {!queryLoading && (
            <>
              {nodeData?._typ !== TEDBOAktivitetTypEnum.AKTOVRIGT &&
                gudstjanstKategori !== '3b' &&
                gudstjanstKategori !== '3h' && (
                  <StatAttributeValue
                    statNodeData={nodeData}
                    attributeName="ANTAL_BARN"
                  />
                )}
              {nodeData?._typ === TEDBOAktivitetTypEnum.AKTOVRIGT &&
                verksamhetsKategori === '8s' && (
                  <StatAttributeValue
                    statNodeData={nodeData}
                    attributeName="ANTAL_TILLFALLEN"
                    label="Antal besökta andra grupper"
                  />
                )}
              {((aktType && validAktTypeForKomm.includes(aktType)) ||
                validGtjCategoriForKomm.includes(gudstjanstKategori)) && (
                <StatAttributeValue
                  statNodeData={nodeData}
                  attributeName="ANTAL_KOMMUNIONER"
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
